import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, MapPin } from 'lucide-react';
import { Page, Reveal, MaskedLines } from '../components/motion';
import { StatusBadge } from '../components/ProjectCard';
import { CTASection } from '../components/CTASection';
import { PROJECTS } from '../data/content';
import { useSEO } from '../hooks/useSEO';

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = PROJECTS.find((p) => p.slug === slug);
  const index = PROJECTS.findIndex((p) => p.slug === slug);
  const next = PROJECTS[(index + 1) % PROJECTS.length];

  useSEO(
    project ? project.name : 'Project',
    project
      ? `${project.name} — ${project.type}, ${project.location}. Status: ${project.status}. A Ehimo Building & Civil Engineering project.`
      : 'Ehimo Building & Civil Engineering project.'
  );

  if (!project) {
    return (
      <Page>
        <section className="pt-52 pb-32 max-w-[1440px] mx-auto px-6 lg:px-12">
          <h1 className="font-display text-4xl uppercase">Project not found</h1>
          <Link to="/projects" data-testid="project-not-found-back" className="mt-6 inline-flex items-center gap-2 text-accent text-sm uppercase tracking-[0.2em]">
            <ArrowLeft size={16} /> All projects
          </Link>
        </section>
      </Page>
    );
  }

  const meta = [
    { label: 'Location', value: project.location },
    { label: 'Project Type', value: project.type },
    { label: 'Status', value: project.status },
    ...(project.client ? [{ label: 'Client', value: project.client }] : []),
  ];

  return (
    <Page>
      <section className="pt-32 lg:pt-40 bg-ink text-white">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <Reveal y={12}>
          <Link
            to="/projects"
            data-testid="project-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-mute hover:text-accent transition-colors"
          >
            <ArrowLeft size={14} /> All projects
          </Link>
        </Reveal>
        <div className="mt-8 flex flex-col lg:flex-row lg:items-end justify-between gap-6 pb-12">
          <div>
            <MaskedLines
              lines={[project.name]}
              lineClassName="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold uppercase tracking-tight leading-[0.95]"
            />
            <Reveal delay={0.3} className="mt-4 flex items-center gap-2 text-sm text-mute uppercase tracking-[0.15em]">
              <MapPin size={14} className="text-accent" /> {project.location}
            </Reveal>
          </div>
          <Reveal delay={0.35}>
            <StatusBadge status={project.status} dark />
          </Reveal>
        </div>
        </div>
      </section>

      <section className="bg-paper text-ink">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 pt-16">
        <Reveal className="group border border-paperline bg-white p-3" data-testid="project-hero-image">
          <div className="overflow-hidden">
            <img
              src={project.image}
              alt={`${project.name} — ${project.type}, ${project.location}`}
              className="w-full max-h-[70vh] object-contain bg-ink saturate-110 contrast-105 group-hover:scale-[1.02] transition-transform duration-700"
            />
          </div>
        </Reveal>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-6 flex items-center gap-3">
              <span className="w-2.5 h-2.5 bg-accent" aria-hidden="true" /> Overview
            </p>
            <p className="text-lg lg:text-xl text-ink leading-relaxed max-w-2xl">
              A {project.type.toLowerCase()} project in {project.location}, {project.status === 'On-Going' ? 'currently on site and in progress' : 'completed and delivered'} by the EhimoBCE team.
            </p>
            <p className="mt-6 text-inkmute leading-relaxed max-w-2xl">
              For full project specifications, references and a consultation on a similar scope, contact our team
              directly.
            </p>
            <Link
              to="/contact"
              data-testid="project-discuss-cta"
              className="mt-10 inline-flex items-center gap-3 bg-ink text-white font-display font-medium uppercase tracking-wide px-8 py-4 text-sm transition-colors duration-300 hover:bg-accent hover:text-ink"
            >
              Discuss your project <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.15} className="border border-paperline divide-y divide-paperline bg-white" data-testid="project-info-panel">
            {meta.map((m) => (
              <div key={m.label} className="flex items-center justify-between px-6 py-5">
                <span className="text-[10px] uppercase tracking-[0.25em] text-inkmute">{m.label}</span>
                <span className="text-sm text-ink text-right">{m.value}</span>
              </div>
            ))}
          </Reveal>
        </div>
      </div>

      {project.gallery && project.gallery.length > 0 && (
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 pb-20 lg:pb-28" data-testid="project-gallery">
          <Reveal className="flex items-center gap-4 mb-10" y={16}>
            <h2 className="font-display text-2xl lg:text-4xl font-semibold uppercase tracking-tight text-ink">Gallery</h2>
            <span className="h-px flex-1 bg-paperline" aria-hidden="true" />
            <span className="text-xs text-inkmute uppercase tracking-[0.2em]">{project.gallery.length + 1} photos</span>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {project.gallery.map((src, i) => (
              <Reveal key={src} delay={i * 0.06} className="group border border-paperline bg-white p-3">
                <div className="overflow-hidden">
                  <img
                    src={src}
                    alt={`${project.name} — site photo ${i + 2}`}
                    loading="lazy"
                    className="img-treated w-full aspect-[4/3] object-cover group-hover:scale-105"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      )}
      </section>

      {next && next.slug !== project.slug && (
        <section className="bg-white text-ink border-t border-paperline">
          <Link
            to={`/projects/${next.slug}`}
            data-testid="project-next-link"
            className="group max-w-[1440px] mx-auto px-6 lg:px-12 py-16 flex items-center justify-between gap-6"
          >
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-inkmute mb-3">Next project</p>
              <p className="font-display text-2xl lg:text-4xl font-semibold uppercase tracking-tight text-ink">
                {next.name}
              </p>
            </div>
            <ArrowUpRight size={32} className="text-ink/25 group-hover:text-ink transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </section>
      )}

      <CTASection title={['Discuss your', 'project.']} />
    </Page>
  );
}
